import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import AlertBanner from "./AlertBanner";
import type { Rewards } from "../../../common/jsonRpcTypes";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { pointsToCurrencyValue } from "../../utils/rewardsEligibility";

/**
 * Which reward message to render.
 *
 * Mirrors the three states of DMN's confirmation fragment
 * (`templates/book/fragments/rewards.html`).
 *
 * `hasAccount` means a DesignMyNight account exists for the booking email — not
 * that the guest is signed in. Collins attaches points by email alone
 * (`BookingRewardTransaction::getUserForTransaction()` resolves
 * `new User($booking->email)`, and `isUserEligibleForReward()` only requires
 * that user to be active), so an existing account earns the points whether or
 * not the guest ever logs in.
 */
export type RewardsBannerVariant = "hasAccount" | "signedOut" | "justSignedUp";

/** External link to the guest's DesignMyNight rewards page. */
function RewardsLink({ href, children }: Readonly<{ href: string; children: React.ReactNode }>): React.ReactElement {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="gcw:underline">
      {children}
    </a>
  );
}

interface RewardsBannerProps {
  readonly variant: RewardsBannerVariant;
  readonly rewards: Rewards;
  readonly currency: string;
  readonly countryCode: string;
  /** The guest's points balance, shown once they are signed in or have signed up. */
  readonly pointsBalance?: number;
  /** Full URL of the guest's DesignMyNight rewards page. Omitted while loading or unconfigured. */
  readonly rewardsUrl?: string;
  /** Extra content rendered beneath the headline message. */
  readonly children?: React.ReactNode;
}

interface SignedUpHeadlineProps {
  /** The welcome balance DMN returned, when it returned one. */
  readonly pointsBalance?: number;
  readonly balanceValueLabel: string;
  /** Formatted redemption threshold, shared with the balance-less copy. */
  readonly redeemValues: { minimumAmount?: number; minimumValue: string };
}

/**
 * Congratulations copy for an account created here on the confirmation.
 *
 * DMN's sign-up response is not guaranteed to carry a `points_balance`, so the
 * welcome-bonus figure is dropped rather than interpolated as an empty value —
 * otherwise the guest reads "a welcome bonus of ( ) Reward Points".
 */
function SignedUpHeadline({
  pointsBalance,
  balanceValueLabel,
  redeemValues,
}: SignedUpHeadlineProps): React.ReactElement {
  if (pointsBalance == null) {
    return (
      <Trans
        i18nKey="booking_rewards_signed_up_no_balance"
        defaults="Congratulations! You've just created an account with Design My Night. Once you hit {{minimumAmount}} points you'll have {{minimumValue}} to redeem against any event on DMN."
        values={redeemValues}
      />
    );
  }

  return (
    <Trans
      i18nKey="booking_rewards_signed_up"
      defaults="Congratulations! You've just created an account with Design My Night. We've even chucked in a welcome bonus of <strong>{{balance}}</strong> ({{balanceValue}}) Reward Points. Once you hit {{minimumAmount}} points you'll have {{minimumValue}} to redeem against any event on DMN."
      values={{ balance: pointsBalance, balanceValue: balanceValueLabel, ...redeemValues }}
      components={{ strong: <strong /> }}
    />
  );
}

interface RewardsAccountLineProps {
  readonly variant: RewardsBannerVariant;
  readonly pointsBalance?: number;
  /** The balance's formatted money value, or an empty string when unknown. */
  readonly balanceValueLabel: string;
  readonly rewardsUrl?: string;
}

/**
 * The line beneath the headline that points the guest at their rewards account.
 *
 * Shown to every guest who has an account, whether it already existed or was
 * created here. A brand-new account needs it most: signing up hands the session
 * to designmynight.com, so the link opens authenticated, and without it the
 * congratulations message is a dead end.
 *
 * Renders nothing for a logged-out guest, or when there is neither a balance to
 * state nor a URL to link to.
 */
function RewardsAccountLine({
  variant,
  pointsBalance,
  balanceValueLabel,
  rewardsUrl,
}: RewardsAccountLineProps): React.ReactElement | null {
  const { t } = useTranslation();

  if (variant === "signedOut") return null;

  // Two cases need the bare link: the just-signed-up copy already states the
  // new balance, and an existing account whose balance we could not read has
  // nothing to state. Both still get a route to the account.
  if (variant === "justSignedUp" || pointsBalance == null) {
    if (!rewardsUrl) return null;
    return (
      <p className="gcw:mt-1">
        <RewardsLink href={rewardsUrl}>
          {t("booking_rewards_view_balance", "Check your balance, earn and spend!")}
        </RewardsLink>
      </p>
    );
  }

  const balance = (
    <Trans
      i18nKey={rewardsUrl ? "booking_rewards_your_balance_link" : "booking_rewards_your_balance"}
      defaults={
        rewardsUrl
          ? "Your balance is <strong>{{balance}}</strong> ({{balanceValue}}). Learn more here."
          : "Your balance is <strong>{{balance}}</strong> ({{balanceValue}})."
      }
      values={{ balance: pointsBalance, balanceValue: balanceValueLabel }}
      components={{ strong: <strong /> }}
    />
  );

  return <p className="gcw:mt-1">{rewardsUrl ? <RewardsLink href={rewardsUrl}>{balance}</RewardsLink> : balance}</p>;
}

/**
 * The purple rewards alert shown across the booking journey.
 *
 * Points are only credited once Collins marks the booking complete and the date
 * has passed (`BookingRewardTransaction::canComplete()`), so the copy
 * consistently frames them as earned by attending rather than by booking.
 *
 * Note the money values are always sterling: Collins'
 * `UserTransaction::pointsToCurrency()` hard-codes GBP.
 */
export default function RewardsBanner({
  variant,
  rewards,
  currency,
  countryCode,
  pointsBalance,
  rewardsUrl,
  children,
}: RewardsBannerProps): React.ReactElement {
  const { t } = useTranslation();
  const money = (value: number) => formatCurrency(value, currency, countryCode);
  const balanceValue = pointsBalance == null ? undefined : pointsToCurrencyValue(pointsBalance, rewards);
  const balanceValueLabel = balanceValue == null ? "" : money(balanceValue);

  const headlineValues = { points: rewards.points, pointsValue: money(rewards.pointsValue) };

  return (
    <AlertBanner
      className="gcw:mb-bs-3 gcw:bg-light-purple gcw:border-0 gcw:text-text-dmn gcw:rounded-alert"
      icon={
        <span className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:w-8 gcw:h-8 gcw:rounded-full gcw:bg-dark-purple">
          <Star size={16} color="#FFFFFF" fill="#FFFFFF" aria-hidden="true" />
        </span>
      }
    >
      <div>
        {variant === "justSignedUp" ? (
          <SignedUpHeadline
            pointsBalance={pointsBalance}
            balanceValueLabel={balanceValueLabel}
            redeemValues={{
              minimumAmount: rewards.redeemMinimumAmount,
              minimumValue: money(rewards.redeemMinimumValue ?? 0),
            }}
          />
        ) : (
          <Trans
            i18nKey={variant === "hasAccount" ? "booking_rewards_earn_attending" : "booking_rewards_earn_by_signing_up"}
            defaults={
              variant === "hasAccount"
                ? "Get free nights on us! Earn <strong>{{points}}</strong> ({{pointsValue}}) points for attending this booking."
                : "You can earn <strong>{{points}}</strong> ({{pointsValue}}) by signing up and attending this booking!"
            }
            values={headlineValues}
            components={{ strong: <strong /> }}
          />
        )}

        {variant === "signedOut" && (
          <p className="gcw:mt-1 gcw:text-body">
            <Trans
              i18nKey="booking_rewards_free_nights_out"
              defaults="(Start earning <strong>FREE</strong> nights out!)"
              components={{ strong: <strong /> }}
            />
          </p>
        )}

        {/*
          A guest whose email already has an account needs to do nothing for the
          points to land, but is still given a route to their balance — the
          equivalent of the "Sign in" link DMN showed when the booking email was
          already registered. A guest who has just signed up gets the same route,
          authenticated by the session hand-off the sign-up performs.
        */}
        <RewardsAccountLine
          variant={variant}
          pointsBalance={pointsBalance}
          balanceValueLabel={balanceValueLabel}
          rewardsUrl={rewardsUrl}
        />

        {variant !== "justSignedUp" && (
          <p className="gcw:mt-1 gcw:text-body">
            {t("booking_rewards_pending_note", "Points are added to your balance once you've attended.")}
          </p>
        )}
        {children}
      </div>
    </AlertBanner>
  );
}
